/**
 * The bar layout model (modular action bars plan, phase 1): a character has
 * bars, a bar has pages and a current page, a page has slots. Every slot is
 * addressed as bar + page + slot.
 *
 * Bar 1 page 1 keeps the slot keys every save already has ("1".."10"), so a
 * save from before this model is already bar 1 page 1 and needs no migration.
 * Any other page's slot is keyed "bar:page:slot" (Mongo map keys cannot hold
 * dots). Only {@link slotKey} and {@link parseSlotKey} know that spelling.
 *
 * Limits are DJ's D4: up to 6 bars, 6 pages per bar, 10 slots per page.
 */

export const MAX_BARS = 6;
export const MAX_PAGES_PER_BAR = 6;
export const SLOTS_PER_PAGE = 10;

/** Where a bar sits (D1). Bar 1 is always `main`; the others pick a fixed spot. */
export type BarSpot = 'main' | 'above' | 'right' | 'left';
const EXTRA_BAR_SPOTS: readonly BarSpot[] = ['above', 'right', 'left'];

export interface BarState {
  id: number;
  /** How many pages the bar has (1..MAX_PAGES_PER_BAR). */
  pages: number;
  /** The page the bar shows and casts from. */
  currentPage: number;
  /** Bar 1 is always visible. */
  visible: boolean;
  spot: BarSpot;
  /** How many slots each page of this bar shows (1..SLOTS_PER_PAGE, default SLOTS_PER_PAGE). */
  slots?: number;
}

export interface SlotRef {
  bar: number;
  page: number;
  slot: number;
}

export function mainBar(): BarState {
  return { id: 1, pages: 1, currentPage: 1, visible: true, spot: 'main' };
}

const inRange = (n: unknown, max: number): n is number => Number.isInteger(n) && (n as number) >= 1 && (n as number) <= max;

/** Resolved slot count for a bar (1..SLOTS_PER_PAGE, defaults to SLOTS_PER_PAGE). */
export function barSlotCount(bar?: Pick<BarState, 'slots'>): number {
  return inRange(bar?.slots, SLOTS_PER_PAGE) ? bar.slots : SLOTS_PER_PAGE;
}

export function isValidSlotRef(ref: SlotRef): boolean {
  return inRange(ref.bar, MAX_BARS) && inRange(ref.page, MAX_PAGES_PER_BAR) && inRange(ref.slot, SLOTS_PER_PAGE);
}

export function slotKey(ref: SlotRef): string {
  return ref.bar === 1 && ref.page === 1 ? String(ref.slot) : `${ref.bar}:${ref.page}:${ref.slot}`;
}

/** The slot a key addresses, or null for a key outside the model. */
export function parseSlotKey(key: string): SlotRef | null {
  const parts = /^\d+$/.test(key) ? ['1', '1', key] : key.split(':');
  if (parts.length !== 3 || parts.some((part) => !/^\d+$/.test(part))) return null;
  const ref = { bar: Number(parts[0]), page: Number(parts[1]), slot: Number(parts[2]) };
  return isValidSlotRef(ref) ? ref : null;
}

/**
 * A saved or received bar list, made valid: bar 1 first and always present,
 * ids unique and in range, pages and current page in range, extra bars on an
 * extra spot. Anything missing (every save from before this model) becomes
 * just bar 1 with one page.
 */
export function normalizeBars(raw: unknown): BarState[] {
  const bars: BarState[] = [mainBar()];
  if (!Array.isArray(raw)) return bars;
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || !inRange((entry as BarState).id, MAX_BARS)) continue;
    const { id, pages, currentPage, visible, spot, slots } = entry as Partial<BarState>;
    const pageCount = inRange(pages, MAX_PAGES_PER_BAR) ? pages : 1;
    const slotCount = inRange(slots, SLOTS_PER_PAGE) ? slots : SLOTS_PER_PAGE;
    const bar: BarState = {
      id: id as number,
      pages: pageCount,
      currentPage: inRange(currentPage, pageCount) ? currentPage : 1,
      visible: id === 1 ? true : visible !== false,
      spot: id === 1 ? 'main' : EXTRA_BAR_SPOTS.includes(spot as BarSpot) ? spot as BarSpot : 'above',
      ...(slotCount !== SLOTS_PER_PAGE ? { slots: slotCount } : {}),
    };
    if (id === 1) bars[0] = bar;
    else if (!bars.some((existing) => existing.id === id)) bars.push(bar);
  }
  return bars;
}

/** Whether a slot belongs to a bar and page that exist in `bars`. */
export function slotRefExists(bars: readonly BarState[], ref: SlotRef): boolean {
  const bar = bars.find((candidate) => candidate.id === ref.bar);
  return Boolean(bar && ref.page <= bar.pages && ref.slot <= barSlotCount(bar) && isValidSlotRef(ref));
}

/**
 * The full layout on the wire, beside `hotbarUpdate`'s flat `slots` (what bar 1
 * shows). `shown` is false for a hidden bar, and for every bar but bar 1 while
 * a session bar (peaceful, race) is up (D3); what is saved does not change.
 */
export interface HotbarLayoutPayload {
  bars: Array<BarState & { shown: boolean }>;
  slots: Array<SlotRef & { abilityId?: string; itemId?: string; type: 'ability' | 'item' | 'empty' }>;
}
